/**
 * Rheina Product Main — wishlist, variant UI sync, accordions, fallback pickers
 */
(function () {
  const SECTION_SELECTOR = '.rheina-product-main-section';

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function initSection(section) {
    if (!section || section.dataset.rheinaPdpInit === 'true') return;
    section.dataset.rheinaPdpInit = 'true';

    initWishlist(section);
    bindVariantDelegation(section);
    bindFallbackDelegation(section);
    initAccordions(section);
    refreshVariantUi(section);

    section.addEventListener('variant:update', (event) => {
      syncColorLabel(section, event);
      refreshVariantUi(section);
    });
  }

  function initWishlist(section) {
    section.querySelectorAll('[data-rheina-wishlist]').forEach((button) => {
      if (button.dataset.rheinaWishlistInit === 'true') return;
      button.dataset.rheinaWishlistInit = 'true';

      button.addEventListener('click', () => {
        const isActive = button.classList.toggle('is-active');
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    });
  }

  function bindVariantDelegation(section) {
    if (section.dataset.rheinaPdpVariantBound === 'true') return;
    section.dataset.rheinaPdpVariantBound = 'true';

    section.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;

      if (target.classList.contains('rheina-pdp__swatch-input')) {
        updateSwatchStates(target.closest('.rheina-pdp__swatches'));
        updateColorLabelFromInput(section, target);
      }

      if (target.classList.contains('rheina-pdp__size-input')) {
        updateSizeStates(target.closest('.rheina-pdp__sizes'));
      }
    });
  }

  function bindFallbackDelegation(section) {
    if (section.dataset.rheinaPdpFallbackBound === 'true') return;
    section.dataset.rheinaPdpFallbackBound = 'true';

    section.addEventListener('click', (event) => {
      const swatch = event.target.closest('[data-rheina-fallback-swatch]');
      if (swatch && section.contains(swatch)) {
        selectFallbackSwatch(section, swatch);
        return;
      }

      const size = event.target.closest('[data-rheina-fallback-size]');
      if (size && section.contains(size)) {
        selectFallbackSize(size);
      }
    });
  }

  function selectFallbackSwatch(section, activeSwatch) {
    const list = activeSwatch.closest('.rheina-pdp__swatches');
    if (!list) return;

    list.querySelectorAll('[data-rheina-fallback-swatch]').forEach((button) => {
      const isSelected = button === activeSwatch;
      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });

    const colorName = activeSwatch.dataset.colorName;
    if (!colorName) return;

    const labelValue = section.querySelector('[data-rheina-color-label-value]');
    if (labelValue) labelValue.textContent = colorName.toUpperCase();
  }

  function selectFallbackSize(activeSize) {
    const list = activeSize.closest('.rheina-pdp__sizes');
    if (!list) return;

    list.querySelectorAll('[data-rheina-fallback-size]').forEach((button) => {
      const isSelected = button === activeSize;
      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });
  }

  function initAccordions(section) {
    const reduceMotion = prefersReducedMotion();

    section.querySelectorAll('.rheina-pdp__accordion').forEach((details) => {
      if (details.dataset.rheinaAccordionInit === 'true') return;
      details.dataset.rheinaAccordionInit = 'true';

      const summary = details.querySelector('.rheina-pdp__accordion-trigger');
      const panel = details.querySelector('.rheina-pdp__accordion-panel');
      if (!(summary instanceof HTMLElement) || !(panel instanceof HTMLElement)) return;

      if (details.open) {
        details.classList.add('is-open');
        panel.style.height = 'auto';
        panel.style.opacity = '1';
        panel.style.paddingBottom = '';
      }

      summary.addEventListener('click', (event) => {
        event.preventDefault();

        const isOpen = details.classList.contains('is-open');
        if (reduceMotion) {
          setAccordionState(details, panel, !isOpen, true);
          return;
        }

        if (isOpen) {
          collapseAccordion(details, panel);
        } else {
          expandAccordion(details, panel);
        }
      });
    });
  }

  function setAccordionState(details, panel, open, instant) {
    details.classList.toggle('is-open', open);
    details.open = open;
    panel.style.height = open ? 'auto' : '0';
    panel.style.opacity = open ? '1' : '0';
    if (instant) details.classList.remove('is-animating');
  }

  function expandAccordion(details, panel) {
    details.classList.add('is-animating', 'is-open');
    details.open = true;
    panel.style.height = '0';
    panel.style.opacity = '0';

    requestAnimationFrame(() => {
      panel.style.height = `${panel.scrollHeight}px`;
      panel.style.opacity = '1';
    });

    const onEnd = (event) => {
      if (event.propertyName !== 'height') return;
      panel.style.height = 'auto';
      details.classList.remove('is-animating');
      panel.removeEventListener('transitionend', onEnd);
    };

    panel.addEventListener('transitionend', onEnd);
  }

  function collapseAccordion(details, panel) {
    details.classList.add('is-animating');
    panel.style.height = `${panel.scrollHeight}px`;

    requestAnimationFrame(() => {
      panel.style.height = '0';
      panel.style.opacity = '0';
    });

    const onEnd = (event) => {
      if (event.propertyName !== 'height') return;
      details.open = false;
      details.classList.remove('is-open', 'is-animating');
      panel.removeEventListener('transitionend', onEnd);
    };

    panel.addEventListener('transitionend', onEnd);
  }

  function refreshVariantUi(section) {
    section.querySelectorAll('.rheina-pdp__swatches').forEach(updateSwatchStates);
    section.querySelectorAll('.rheina-pdp__sizes').forEach(updateSizeStates);

    const checkedColor = section.querySelector('.rheina-pdp__swatch-input:checked');
    if (checkedColor instanceof HTMLInputElement) {
      updateColorLabelFromInput(section, checkedColor);
    }
  }

  function updateSwatchStates(list) {
    if (!list) return;
    list.querySelectorAll('.rheina-pdp__swatch-input').forEach((input) => {
      const swatch = input.nextElementSibling;
      if (!(swatch instanceof HTMLElement)) return;
      swatch.classList.toggle('is-selected', input.checked);
    });
  }

  function updateSizeStates(list) {
    if (!list) return;
    list.querySelectorAll('.rheina-pdp__size-input').forEach((input) => {
      const size = input.nextElementSibling;
      if (!(size instanceof HTMLElement)) return;
      size.classList.toggle('is-selected', input.checked);
    });
  }

  function updateColorLabelFromInput(section, input) {
    const labelValue = section.querySelector('[data-rheina-color-label-value]');
    if (!labelValue || !(input instanceof HTMLInputElement)) return;
    const colorName = input.dataset.colorName || input.value;
    if (colorName) labelValue.textContent = colorName.toUpperCase();
  }

  function syncColorLabel(section, event) {
    const html = event.detail?.data?.html;
    if (!html) return;

    const nextLabel = html.querySelector('[data-rheina-color-label-value]');
    const currentLabel = section.querySelector('[data-rheina-color-label-value]');
    if (nextLabel && currentLabel) {
      currentLabel.textContent = nextLabel.textContent;
    }
  }

  function initAll(root = document) {
    root.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof HTMLElement) initAll(event.target);
  });
})();
